import { useCallback, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { MantineProvider } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

import { lightTheme } from '~/config/theme';
import { BRANDS } from '~/domains/auth/consts';
import SignInActions from '~/domains/auth/components/SignInActions';
import type { SignInFormValues } from '~/domains/auth/components/SignInActions/types';
import useRedirectAfterAuth from '~/domains/auth/hooks/useRedirectAfterAuth';
import { useEORedirect } from '~/domains/auth/hooks/useEORedirect';
import { useUserSchema } from '~/domains/auth/hooks/useUserSchema';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { SplashLoader } from '~/domains/common/components/SplashLoader/SplashLoader';
import useLogin from '~/domains/auth/hooks/useLogin';

import useStyles from './SignIn.styles';

function SignIn() {
  const { t } = useTranslation('auth');
  const [isLoggingInitialised, setIsLoggingInitialised] = useState(false);
  const router = useRouter();
  const { classes } = useStyles();
  const { brand, platform } = useGetBranding();
  const { signInSchema } = useUserSchema();
  const handleRedirectAfterAuth = useRedirectAfterAuth();
  const { shouldRedirect: shouldRedirectToEOSite, eoRedirectUrl } =
    useEORedirect(!isLoggingInitialised);

  const redirectTo = router.query.redirectTo as string;
  const status = router.query.status as string;
  const resetSuccess = router.query.resetSuccess as string;
  const accessToken = Cookies.get('accessToken');

  const form: UseFormReturnType<SignInFormValues> = useForm<SignInFormValues>({
    initialValues: {
      emailOrUsername: '',
      password: '',
    },
    validate: zodResolver(signInSchema),
    validateInputOnChange: true,
  });

  const { handleLogin, isLoggingIn } = useLogin({
    onSuccess: (_, redirectUrl) => {
      handleRedirectAfterAuth(redirectUrl, true);
    },
  });

  // User Already logged in. Select appropriate redirect URL
  useEffect(() => {
    // Prevent redirects after login has been initialised (it has it's own redirect handler)
    if (isLoggingInitialised) return;

    // Using access token specifically, because EO login doesn't set the user in the context or local storage
    if (accessToken) {
      // We need two separate checks, because we might have to wait for the redirectURL (waiting on SUT)
      if (shouldRedirectToEOSite && eoRedirectUrl) handleRedirectAfterAuth(eoRedirectUrl, true);
      if (!shouldRedirectToEOSite) handleRedirectAfterAuth(redirectTo);
    }
  }, [redirectTo, shouldRedirectToEOSite, eoRedirectUrl, accessToken, isLoggingInitialised]);

  const showErrorRYPNotification = useCallback(() => {
    const NOTIFICATION_CONFIGS = {
      [BRANDS.RYP]: {
        authSSODeprecated: {
          title: t('ssoLoginDisabled.title'),
          message: t('ssoLoginDisabled.copy'),
        },
        authError: {
          title: t('errors.authError.title'),
          message: t('errors.authError.copy'),
        },
        invalidSSO: {
          title: t('errors.generic.title'),
          message: t('errors.generic.copy'),
        },
        accountNotFound: {
          title: t('errors.ssoAccountNotFound.title'),
          message: t('errors.ssoAccountNotFound.copy'),
        },
      },
      [BRANDS.OFP]: {
        authError: {
          title: t('errors.generic.title'),
          message: t('errors.generic.copy'),
        },
      },
    };

    const config = NOTIFICATION_CONFIGS[brand]?.[status as keyof typeof NOTIFICATION_CONFIGS];
    if (config) {
      showNotification({
        title: config.title,
        message: config.message,
        color: 'red',
        autoClose: 5000,
      });
    }
  }, [brand, status, t]);

  useEffect(showErrorRYPNotification, [showErrorRYPNotification]);

  if (accessToken) {
    return (
      <MantineProvider theme={lightTheme}>
        <SplashLoader />
      </MantineProvider>
    );
  }

  return (
    <MantineProvider theme={lightTheme}>
      <div className={classes.signInContainer}>
        <SignInActions
          signInProps={{
            handleFormSubmit: form.onSubmit(({ emailOrUsername, password }) => {
              setIsLoggingInitialised(true);
              handleLogin({ email: emailOrUsername, password });
            }),
            emailFieldProps: form.getInputProps('emailOrUsername'),
            passwordFieldProps: form.getInputProps('password'),
            isFormLoading: isLoggingIn,
          }}
          resetSuccess={resetSuccess}
          brand={brand}
          platform={platform}
        />
      </div>
    </MantineProvider>
  );
}

export default SignIn;
